import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getTransactionTableData, getTransactionFilters } from "src/api/bi";
import { resetStore } from "src/store/actions";
import { formatToCurrency, getComponentKey } from "src/utils/genericFunctions";
import stringToNumber from "src/utils/stringToNumber";
import { TransactionTableInitialState } from "../interface";

const initialState: TransactionTableInitialState = {
  data: [],
  recordsCount: 0,
  status: "idle",
  transactionFilters: [],
  register: [],
  cashierId: [],
  cashierIdOption: [],
  registerOption: [],
  error: null
};

export const fetchTransactionTableData = createAsyncThunk(
  "itemDetails/fetchTransactionTableData",
  async ({ itemName, ...payload }: any) => {
    const response = await getTransactionTableData(payload);
    const data = response.data.map((data: any) => ({ ...data, itemName }));
    return Promise.resolve({ ...response, data });
  }
);

export const fetchTransactionFilters = createAsyncThunk(
  "itemDetails/fetchTransactionFilters",
  ({
    itemId,
    storeId,
    fromDate,
    toDate
  }: {
    itemId: string;
    storeId: string;
    fromDate: string;
    toDate: string;
  }) => getTransactionFilters({ itemId, storeId, fromDate, toDate })
);

const transactionTableData = createSlice({
  name: "transactionTable",
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(resetStore, (state, action) => {
        return initialState;
      })
      .addCase(fetchTransactionTableData.pending, (state) => {
        state.status = "pending";
      })
      .addCase(
        fetchTransactionTableData.fulfilled,
        (state, { payload, meta }) => {
          const { itemId } = meta.arg;
          state.data = payload.data.map((item: any) => {
            const overallRetail = formatToCurrency(
              stringToNumber(item.overallRetail)
            );
            const basketRetail = formatToCurrency(
              stringToNumber(item.basketRetail)
            );
            return {
              ...item,
              itemId,
              cashierId: item.cashierId.replace(/^0+/, ""),
              registerId: item.registerId.replace(/^0+/, ""),
              overallRetail,
              basketRetail,
              key: getComponentKey()
            };
          });
          state.recordsCount = Number(payload.recordsCount) || 0;

          const registers: any = new Set();

          payload.data.forEach(
            (filter: {
              registerId: string;
              cashierId: string;
              cashierName: string;
            }): string => registers.add(filter.registerId)
          );
          state.registerOption = [...registers]
            .map((register: string) => ({
              id: register.replace(/^0+/, ""),
              name: register.replace(/^0+/, "")
            }))
            .sort((itemA: any, itemB: any) => itemA.id - itemB.id);

          const cashierId: any = new Set();
          payload.data.map(
            (filter: {
              registerId: string;
              cashierId: string;
              cashierName: string;
            }) =>
              cashierId.add(
                `${filter.cashierId.replace(/^0+/, "")}${filter.cashierName ? "-" : ""
                }${filter.cashierName}`
              )
          );
          state.cashierIdOption = [...cashierId]
            .map((cashier: string) => ({
              id: cashier.split("-")[0],
              name: cashier
            }))
            .sort((itemA: any, itemB: any) => itemA.id - itemB.id);
          state.status = "idle";
        }
      )
      .addCase(fetchTransactionTableData.rejected, (state) => {
        state.status = "error";
        state.data = [];
      });
  }
});

export const selectTransactionTableData = ({
  transactionTableData
}: {
  transactionTableData: TransactionTableInitialState;
}) => transactionTableData.data;

export const selectTransactionTableRecordsCount = ({
  transactionTableData
}: {
  transactionTableData: TransactionTableInitialState;
}) => {
  return transactionTableData.recordsCount;
};

export const selectTransactionTableApiStatus = ({
  transactionTableData
}: {
  transactionTableData: TransactionTableInitialState;
}) => transactionTableData?.status;

export const selectstateError = ({
  transactionTableData
}: {
  transactionTableData: TransactionTableInitialState;
}) => transactionTableData.error;

export const selectTransactionFilters = ({
  transactionTableData
}: {
  transactionTableData: TransactionTableInitialState;
}) => transactionTableData.transactionFilters;

export const getTransactionFilterOptions = ({
  transactionTableData
}: {
  transactionTableData: TransactionTableInitialState;
}) => ({
  cashierIdOption: transactionTableData.cashierIdOption,
  registerOption: transactionTableData.registerOption
});

export default transactionTableData.reducer;
